<script setup>
import {computed, inject, ref} from "vue";
import useComment, {USE_COMMENT_KEY} from "./CommentComposition";
import useNickname, {USE_NICKNAME_KEY} from "./NicknameComposition";
import NicknameContainer from "./NicknameContainer.vue";
const siteUrl = document.querySelector('meta[name="site-url"]').content;

const props = defineProps({
  commentId: {
    type: String,
    required: true
  },
  parentId: {
    type: Number,
    required: true
  },
  nicknameId: {
    type: Number,
    default: 0
  },
  heightTransition: {
    type: Object,
    required: true
  }
});
const emits = defineEmits(['open-thread']);

//コンポジションを定義、配信
const composition = inject(USE_COMMENT_KEY, useComment());
const nicknameComposition = inject(USE_NICKNAME_KEY, useNickname(siteUrl));

//投稿文
const content = ref('');

//エラー文字列配列
const errors = ref([]);

//本文の文字数を正確に数える
// https://qiita.com/ka215/items/d059a78e29adef3978b5#6-%E3%83%9E%E3%83%AB%E3%83%81%E3%83%90%E3%82%A4%E3%83%88%E6%96%87%E5%AD%97%E3%81%AB%E5%AF%BE%E5%BF%9C%E3%81%97%E3%81%9F%E6%96%87%E5%AD%97%E5%88%97%E5%87%A6%E7%90%86
const contentLength = computed(() => {
  //指定された範囲にサロゲートペアが存在するかを boolean で返す
  //upper は 1 バイト目、lower に 2 バイト目を指定する
  //UTF の場合、通常は 2 バイトで一文字だが、サロゲートペアの場合は 4 バイトで一文字を表す
  const isSurrogatePair = (upper, lower) => 0xD800 <= upper && upper <= 0xDBFF && 0xDC00 <= lower && lower <= 0xDFFF;

  //最終的に返す文字数
  let result = 0;

  //サロゲートペアがあればそれを 2 文字ではなく 1 文字としてカウントしつつ文字数を返す
  for (let i = 0; i < content.value.length; i++,result++) {
    //サロゲートペアでない文字は一文字とカウントする
    if ( ! isSurrogatePair(content.value.charCodeAt(i), content.value.length > (i + 1) ? content.value.charCodeAt(i + 1) : 0)) {
      continue;
    }

    //サロゲートペアなら一文字ぶん多くカウントする
    i++;
  }

  //結果文字列の数を返す
  return result;
});

//本文の文字数が 5 文字以上 1000 文字以下であるか
const validContentLength = computed(() => contentLength.value >= 1 && contentLength.value <= 1000);

//確認画面へ進む or 送信
const submit = async () => {
  //エラーを空にする
  errors.value = [];

  //事前にスレッドを開いておく
  emits('open-thread');

  //スレッドが開いてからコメント投稿を行う
  setTimeout(async () => {
    //サーバーに依頼
    const result = await composition.setComment(props.parentId === 0 ? nicknameComposition.state.nicknameId : props.nicknameId, content.value, props.parentId);

    //成功したら閉じる
    if (result === null) {
      props.heightTransition.close();
    }

    //失敗したらエラーを表示
    else {
      errors.value = result;
    }
  }, 200);
};
</script>

<template>
  <slot></slot>
  <teleport :to="'#'+commentId">
    <transition
      name="comment"
      @enter="heightTransition.onEnter"
      @after-enter="heightTransition.onAfter"
      @leave="heightTransition.onLeave"
      @after-leave="heightTransition.onAfter"
    >
      <div v-if="heightTransition.isOpen.value">
        <div :class="{lyPd8: parentId !== 0, 'js-login-comment': composition.state.userId === 0}">
          <div class="lyMb8">
            <NicknameContainer
              v-if="parentId === 0"
              :is-login="composition.state.userId !== 0"
              :style="composition.state.userId === 0 ? 'pointer-events:none' : ''"
            />
            <p
              v-else
              class="lyPt10 lyPb10 lyPr8 lyPl8 pFontBold pFontBlue pBorder1pxSolidGray20 pBorderRadius2px"
            >自分の返信</p>
          </div>
          <div
            :style="composition.state.userId === 0 ? 'pointer-events:none' : ''"
            class="lyMb8"
          >
            <div :class="{lyOverFlewHidden: true, pBorderRadius2px: true, pBorder1pxSolidGray20: parentId !== 0}">
              <textarea
                v-model="content"
                class="js-flexibleTextArea lyW100 lyPd8 pBackColorWhite"
                placeholder="コメントを入力する"
                rows="10"
              ></textarea>
              <div class="lyPd8 lyHorizontal pFont10 pBackColorWhite">
                <div :class="{lyHorizontal: true, pFontRed: ! validContentLength, pFontGreen: validContentLength}">
                  <p class="lyMr4">現在</p>
                  <p class="lyMr4">{{new Intl.NumberFormat().format(contentLength)}}</p>
                  <p class="lyMr4">文字入力</p>
                </div>
                <p class="pFontGray30">※最大500文字入力可能</p>
              </div>
            </div>
          </div>
          <div :style="composition.state.userId === 0 ? 'pointer-events:none' : ''">
            <p
              v-for="error in errors"
              class="lyMb8 pFont12 pFontMedium pFontRed lyHidden"
            >{{error}}</p>
          </div>
          <p
            :style="composition.state.userId === 0 ? 'pointer-events:none' : ''"
            class="pageBoardPostBtn lyHorizontalCnt"
          >
            <button
              type="submit"
              :class="{
              lyHvCnt: true,
              lyW100: true,
              lyPt12: true,
              lyPb12: true,
              lyTextCnt: true,
              pFont14: true,
              pFontBold: true,
              pFontWhite: true,
              pBackColorLightBlue: true,
              pBorderRadius2px: true,
              pHoverSlideChange: true,
              lyMaxW400px: parentId === 0,
              isDisabled: contentLength < 1 || contentLength > 1000 || (parentId === 0 && nicknameComposition.state.nicknameId === -2)
            }"
              @click="submit"
            >{{parentId === 0 ? 'コメント送信' : '返信する'}}</button>
          </p>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<style scoped>
.comment-enter-active,
.comment-leave-active {
  transition: height 0.5s ease;
  height: auto;
}
</style>
